import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme, alpha } from '@mui/material/styles';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import { prettyMetric } from '../util/metricLabels';
import {
  getMySessionMetricComparison,
  getSessionMetricComparison,
  type DebriefPoint,
  type MetricComparisonRow,
  type SessionMetricComparison,
} from '../api/sessionMetrics';
import { performanceTier, type Tier } from './SessionMetricScorecard';

/**
 * AI Coach card (Version D · Pass 2). Replaces the flat "Insight" placeholder
 * that used to sit beside the session scorecard with a prominent, feature-y
 * card that surfaces the session's single biggest coaching opportunity.
 *
 * Until the AI-insights backend lands, the copy is DERIVED — not invented:
 * we grade each metric on the same performance-tier ladder the scorecard
 * uses and pick the one furthest from its next benchmark, with a real,
 * computed shortfall. No fabricated percentages; when every metric is already
 * at/above the expert cohort we say so, and when nothing is scored yet we say
 * that too. The richer, generative narration slots in here later.
 *
 * (This fetches the same comparison the adjacent scorecard does — a second
 * cheap GET — rather than threading the data down through both columns.)
 */

interface Opportunity {
  metric: string;
  tier: Tier; // 'bad' | 'good' only reach here
  /** Which benchmark the gap is measured against, in prose. */
  targetLabel: string;
  /** Short benchmark name for the "next goal" line ("practice-ready"). */
  nextLevel: string;
  /** The benchmark value to aim for, + its unit + direction — so the goal is
   *  concrete ("≥ 240 s") rather than a bare percentage. */
  targetValue: number;
  unit: string | null;
  direction: string;
  /** Shortfall as a percentage of the target value (rounded, ≥1). */
  pct: number;
}

/** Percentage shortfall of `you` from `target`, floored at 1 so a real gap
 *  never rounds away to "0% short". */
function shortfallPct(you: number, target: number): number {
  const denom = Math.abs(target) || Math.abs(you) || 1;
  return Math.max(1, Math.round((Math.abs(target - you) / denom) * 100));
}

/** Concrete target for the "next goal" line: rounded value + unit. */
function formatTarget(v: number, unit: string | null): string {
  const n = Math.abs(v) >= 10 ? Math.round(v) : Math.round(v * 100) / 100;
  return unit ? `${n} ${unit}` : `${n}`;
}

/** One metric's movement vs the participant's recent prior sessions. */
interface Trend {
  metric: string;
  pct: number; // absolute % change, rounded
  improved: boolean; // direction-aware (a drop in idle-time is an improvement)
}

/** Session-over-session movers: this session's value vs `recent_mean`, per
 *  metric, direction-aware, filtered to changes worth mentioning. Largest
 *  movement first. */
function computeTrends(
  rows: MetricComparisonRow[],
  minPct = 3,
): Trend[] {
  const out: Trend[] = [];
  for (const row of rows) {
    const you = row.session_value;
    const prev = row.recent_mean;
    if (you === null || prev == null || row.direction === 'none') continue;
    const denom = Math.abs(prev) || Math.abs(you) || 1;
    const deltaPct = Math.round(((you - prev) / denom) * 100);
    if (Math.abs(deltaPct) < minPct) continue;
    out.push({
      metric: prettyMetric(row.metric),
      pct: Math.abs(deltaPct),
      improved: row.direction === 'lower' ? deltaPct < 0 : deltaPct > 0,
    });
  }
  return out.sort((a, b) => b.pct - a.pct);
}

/** A synthesized one-line read. Leads with a real improvement when there is
 *  one ("did I improve?"), else summarizes strengths. */
function coachHeadline(
  strengths: string[],
  topImprovement: Trend | undefined,
): string {
  if (topImprovement) {
    return `You improved ${topImprovement.metric} ${topImprovement.pct}% — here's your next gain.`;
  }
  if (strengths.length >= 2) {
    return `Strong across ${strengths.length} measures — one area to push.`;
  }
  if (strengths.length === 1) {
    return `${strengths[0]} is expert-level — here's your next gain.`;
  }
  return "Here's where to focus next.";
}

/** "Since your last N sessions" — the biggest movers as up/down chips. Green
 *  when improved (direction-aware), muted when it slipped. */
function TrendBlock({
  trends,
  count,
}: {
  trends: Trend[];
  count: number;
}) {
  const theme = useTheme();
  return (
    <>
      <Divider sx={{ my: 1.25 }} />
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          fontWeight: 700,
          color: 'text.secondary',
          mb: 0.75,
        }}
      >
        Since your last {count} session{count === 1 ? '' : 's'}
      </Typography>
      <Stack
        direction="row"
        spacing={0.75}
        sx={{ flexWrap: 'wrap', rowGap: 0.75 }}
      >
        {trends.slice(0, 3).map((t) => (
          <Chip
            key={t.metric}
            size="small"
            label={`${t.metric} ${t.improved ? '↑' : '↓'}${t.pct}%`}
            sx={{
              fontWeight: 700,
              color: t.improved
                ? theme.palette.success.dark
                : theme.palette.text.secondary,
              bgcolor: t.improved
                ? alpha(theme.palette.success.main, 0.14)
                : alpha(theme.palette.text.primary, 0.06),
            }}
          />
        ))}
      </Stack>
    </>
  );
}

/** The single biggest coaching opportunity across the session's metrics, or
 *  null when nothing is below its benchmark (i.e. everything's expert-level,
 *  or nothing is scored against a cohort yet). `allStrong` disambiguates the
 *  two null cases so the card can celebrate vs. stay quiet. */
function pickOpportunity(rows: MetricComparisonRow[]): {
  best: Opportunity | null;
  allStrong: boolean;
  strengths: string[];
} {
  let best: Opportunity | null = null;
  let bestScore = -Infinity;
  let anyGradeable = false;
  const strengths: string[] = [];

  for (const row of rows) {
    const you = row.session_value;
    if (you === null || row.direction === 'none' || row.references.length === 0) {
      continue;
    }
    const tier = performanceTier(you, row.references, row.direction);
    if (tier === null) continue;
    anyGradeable = true;
    if (tier === 'great' || tier === 'elite') {
      // At/above the expert cohort — a genuine strength worth calling out.
      strengths.push(prettyMetric(row.metric));
      continue;
    }

    // References arrive novice → expert. The expert benchmark is the last;
    // the practice-ready benchmark is tier 1 when present.
    const expert = row.references[row.references.length - 1];
    const practiceReady =
      row.references.find((r) => r.tier === 1) ?? row.references[0];
    const single = row.references.length < 2;

    const target = tier === 'bad' ? practiceReady : expert;
    const targetLabel =
      tier === 'bad'
        ? single
          ? 'the reference cohort'
          : 'the practice-ready benchmark'
        : 'the expert cohort';
    const nextLevel =
      tier === 'bad' ? (single ? 'the reference' : 'practice-ready') : 'expert';
    const pct = shortfallPct(you, target.value);
    // Below practice-ready is a higher priority than merely short of expert;
    // within a tier, a larger shortfall wins.
    const score = (tier === 'bad' ? 100_000 : 0) + pct;
    if (score > bestScore) {
      bestScore = score;
      best = {
        metric: row.metric,
        tier,
        targetLabel,
        nextLevel,
        targetValue: target.value,
        unit: row.unit,
        direction: row.direction,
        pct,
      };
    }
  }

  return { best, allStrong: anyGradeable && best === null, strengths };
}

/** "What's working" — the metrics this session graded at expert level or
 *  above, as green chips. Real strengths derived from the tier ladder, not
 *  fabricated praise. */
function StrengthsBlock({ strengths }: { strengths: string[] }) {
  const theme = useTheme();
  return (
    <>
      <Divider sx={{ my: 1.25 }} />
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          fontWeight: 700,
          color: 'success.main',
          mb: 0.75,
        }}
      >
        What's working
      </Typography>
      <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
        {strengths.map((s) => (
          <Chip
            key={s}
            size="small"
            label={s}
            sx={{
              fontWeight: 700,
              color: theme.palette.success.dark,
              bgcolor: alpha(theme.palette.success.main, 0.14),
            }}
          />
        ))}
      </Stack>
    </>
  );
}

export function AiCoachCard({
  tenantId,
  sessionId,
}: {
  /** Tenant-admin drill-down passes it; the participant view omits it and
   *  the /me endpoint resolves the owning tenant. Mirrors the scorecard. */
  tenantId?: string;
  sessionId: string;
}) {
  const theme = useTheme();
  const [data, setData] = useState<SessionMetricComparison | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    setData(null);
    setFailed(false);
    const p = tenantId
      ? getSessionMetricComparison(tenantId, sessionId)
      : getMySessionMetricComparison(sessionId);
    p.then((d) => alive && setData(d)).catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [tenantId, sessionId]);

  const { best, allStrong, strengths } = data
    ? pickOpportunity(data.rows)
    : { best: null, allStrong: false, strengths: [] as string[] };
  const recentCount = data?.recent_session_count ?? 0;
  const trends = data && recentCount > 0 ? computeTrends(data.rows) : [];
  const topImprovement = trends.find((t) => t.improved);
  // When the worker has generated an LLM read for this session, it leads;
  // the deterministic derived copy below is the permanent fallback for when
  // coaching is disabled, ungenerated, or the model call failed.
  const coaching = data?.coaching ?? null;

  // Accent + wash: a soft brand-teal feature card that reads as more than a
  // muted note. Success-green when the session is already strong.
  const accent = allStrong ? theme.palette.success.main : theme.palette.primary.main;

  return (
    <Card
      sx={{
        borderColor: alpha(accent, 0.5),
        borderTop: '3px solid',
        borderTopColor: accent,
        background: `linear-gradient(160deg, ${alpha(accent, 0.1)} 0%, ${alpha(
          accent,
          0.02,
        )} 60%, transparent 100%)`,
      }}
    >
      <CardContent>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ color: accent, mb: 1.25 }}
        >
          <AutoAwesomeIcon fontSize="small" />
          <Typography
            variant="overline"
            sx={{ fontWeight: 800, letterSpacing: '0.1em' }}
          >
            AI Coach
          </Typography>
        </Stack>

        {!data && !failed && (
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ py: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Reading this session…
            </Typography>
          </Stack>
        )}

        {failed && (
          <Typography variant="body2" color="text.secondary">
            Couldn't load this session's coaching read just now.
          </Typography>
        )}

        {data && coaching && (
          <>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                fontWeight: 700,
                color: 'text.secondary',
                mb: 0.5,
              }}
            >
              Today's coaching
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 800, color: accent, lineHeight: 1.25 }}
            >
              {coaching.headline}
            </Typography>
            {coaching.bullets.length > 0 && (
              <Stack component="ul" spacing={0.5} sx={{ mt: 0.75, mb: 0, pl: 2.5 }}>
                {coaching.bullets.map((b, i) => (
                  <Typography key={i} component="li" variant="body2">
                    {b}
                  </Typography>
                ))}
              </Stack>
            )}
            {coaching.next_goal && (
              <Box
                sx={{
                  mt: 1,
                  px: 1.25,
                  py: 0.75,
                  borderRadius: 1,
                  bgcolor: alpha(accent, 0.1),
                }}
              >
                <Typography variant="body2">
                  <Box component="span" sx={{ fontWeight: 800 }}>
                    Next goal:
                  </Box>{' '}
                  {coaching.next_goal}
                </Typography>
              </Box>
            )}
            {trends.length > 0 && (
              <TrendBlock trends={trends} count={recentCount} />
            )}
            {strengths.length > 0 && <StrengthsBlock strengths={strengths} />}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 1.25 }}
            >
              Written by your AI coach from this session's metrics vs your
              reference cohort.
            </Typography>
          </>
        )}

        {data && !coaching && best && (
          <>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                fontWeight: 700,
                color: 'text.secondary',
                mb: 0.5,
              }}
            >
              Today's coaching
            </Typography>
            {/* Synthesized headline, then the specific gap, then a concrete
                next goal — a fuller read than the prior one-liner. */}
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 800, color: accent, lineHeight: 1.25 }}
            >
              {coachHeadline(strengths, topImprovement)}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.75 }}>
              Your biggest gain is{' '}
              <Box component="span" sx={{ fontWeight: 800 }}>
                {prettyMetric(best.metric)}
              </Box>{' '}
              — about{' '}
              <Box component="span" sx={{ fontWeight: 800, color: accent }}>
                {best.pct}%
              </Box>{' '}
              away from {best.targetLabel}.
            </Typography>
            <Box
              sx={{
                mt: 1,
                px: 1.25,
                py: 0.75,
                borderRadius: 1,
                bgcolor: alpha(accent, 0.1),
              }}
            >
              <Typography variant="body2">
                <Box component="span" sx={{ fontWeight: 800 }}>
                  Next goal:
                </Box>{' '}
                reach {best.nextLevel} on {prettyMetric(best.metric)} (
                {best.direction === 'lower' ? '≤' : '≥'}{' '}
                {formatTarget(best.targetValue, best.unit)}).
              </Typography>
            </Box>
            {trends.length > 0 && (
              <TrendBlock trends={trends} count={recentCount} />
            )}
            {strengths.length > 0 && <StrengthsBlock strengths={strengths} />}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 1.25 }}
            >
              Derived from this session vs your reference cohort.
            </Typography>
          </>
        )}

        {data && !coaching && !best && allStrong && (
          <>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 800, color: accent, lineHeight: 1.25 }}
            >
              Strong session 🎯
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.75 }}>
              You're at or above the expert cohort on every measured metric this
              session. Keep the streak going.
            </Typography>
            {trends.length > 0 && (
              <TrendBlock trends={trends} count={recentCount} />
            )}
            {strengths.length > 0 && <StrengthsBlock strengths={strengths} />}
          </>
        )}

        {data && !coaching && !best && !allStrong && (
          <Typography variant="body2" color="text.secondary">
            A plain-language read on this session — where you sit vs the
            reference cohort, and what to work on next — appears here once its
            metrics are scored against a cohort.
          </Typography>
        )}
        {data?.debrief && data.debrief.length > 0 && (
          <DebriefSection points={data.debrief} />
        )}
      </CardContent>
    </Card>
  );
}

const SUPPORT_LABEL: Record<DebriefPoint['support'], string> = {
  supported: 'Supported by the data',
  differs: 'The data differs',
  no_signal: 'No clear signal in the data',
};

function stamp(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** The coach's points from the debrief (AI-COACH-FROM-COACHING-TRANSCRIPT):
 *  each with the coach's own words — quoted verbatim from the transcript —
 *  and what this session's metrics show about it. */
export function DebriefSection({ points }: { points: DebriefPoint[] }) {
  return (
    <Box sx={{ mt: 2 }}>
      <Divider sx={{ mb: 1.5 }} />
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          fontWeight: 700,
          color: 'text.secondary',
          mb: 1,
        }}
      >
        From your debrief
      </Typography>
      <Stack spacing={1.5}>
        {points.map((p, i) => (
          <Box key={i}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {p.point}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontStyle: 'italic', mt: 0.25 }}
            >
              “{p.quote}” — debrief, {stamp(p.start_s)}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
              <Chip
                size="small"
                variant="outlined"
                color={p.support === 'supported' ? 'success' : p.support === 'differs' ? 'warning' : 'default'}
                label={SUPPORT_LABEL[p.support] ?? SUPPORT_LABEL.no_signal}
              />
              {p.evidence && (
                <Typography variant="caption" color="text.secondary">
                  {p.evidence}
                </Typography>
              )}
            </Stack>
          </Box>
        ))}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25 }}>
        Your coach&apos;s words, from the debrief transcript. Pick the debrief under
        Transcript above to hear it.
      </Typography>
    </Box>
  );
}
